import { businessIdsHtml, escapeHtml as esc, printHtml, type DocumentSalon } from '../../utils/invoice'
import { brandColors } from '../../data/brandColors'
import { vatOfLines } from '../../core/rules/kassa'
import { money } from '../../core/format/currency'
import { fullDate, fromIso } from '../../core/format/dates'
import { clock, refundMethodLabel } from './kassaLabels'
import type { SaleReturn } from '../../core/types/kassa'

type Salon = DocumentSalon

/**
 * De creditnota van een retour op een kassabon: eigen nummer uit de creditnotareeks, verwijzing
 * naar de oorspronkelijke bon, de teruggenomen regels en de btw per tarief negatief. Met de echte
 * server wordt dit een PDF, net als de kassabon.
 */
export function returnCreditNoteHtml(r: SaleReturn, salon: Salon): string {
  const neg = (cents: number) => (cents === 0 ? money(0) : `− ${money(cents)}`)
  const vat = vatOfLines(r.lines.map((l) => ({ vatRate: l.vatRate, totalCents: l.totalCents })))
  const back = r.method === 'tegoed' ? `Bijgeschreven als tegoed bij ${salon.name}.` : `Terugbetaald: ${refundMethodLabel[r.method].toLowerCase()}.`
  const rows = r.lines
    .map((l) => `<tr><td>${l.qty > 1 ? `${l.qty} × ` : ''}${esc(l.name)}${l.qty > 1 ? `<div class="muted small">${esc(money(l.unitCents))} per stuk</div>` : ''}</td><td class="r">${esc(neg(l.totalCents))}</td></tr>`)
    .join('')

  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>Creditnota ${esc(r.number)}</title>
<style>
@page{margin:12mm}
body{font-family:Inter,system-ui,sans-serif;color:${brandColors.ink};margin:40px auto;max-width:460px;font-size:14px;line-height:1.5}
@media print{body{margin:0 auto}}
h1{font-family:Fraunces,Georgia,serif;font-weight:300;font-size:26px;margin:24px 0 4px}
.muted{color:${brandColors.muted}}.small{font-size:12px}
table{width:100%;border-collapse:collapse;margin-top:20px}td{text-align:left;padding:7px 0;border-bottom:1px solid ${brandColors.line};vertical-align:top}
td.r{text-align:right;white-space:nowrap;padding-left:12px}.tot td{border:0;padding:3px 0}.big td{font-size:16px;padding-top:10px}
</style></head><body>
<strong>${esc(salon.name)}</strong><div class="muted small">${esc(salon.address)} · ${esc(salon.email)}<br>${businessIdsHtml(salon, ' · ')}</div>
<h1>Creditnota</h1>
<div class="muted">${esc(r.number)} · ${esc(fullDate(fromIso(r.day)))} ${esc(clock(r.at))} · ${esc(r.customerName)}</div>
<div class="muted small">Bij bon ${esc(r.saleNumber)} van ${esc(fullDate(fromIso(r.saleDay)))}</div>
<table><tbody>${rows}</tbody></table>
<table><tbody class="tot">
<tr class="big"><td>Totaal</td><td class="r">${esc(neg(r.refundCents))}</td></tr>
${vat.map((v) => `<tr><td class="muted">Waarvan btw ${v.vatRate}%</td><td class="r">${esc(neg(v.vatCents))}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:20px">${esc(back)}</p>
<p class="muted small" style="margin-top:12px">Reden: ${esc(r.reason)}</p>
</body></html>`
}

export const printReturnCreditNote = (r: SaleReturn, salon: Salon) => printHtml(returnCreditNoteHtml(r, salon), `creditnota-${r.number}.html`, 'Creditnota')
